import { useReact19UniqueKeyCompatibility } from '@/hooks/use-react19-unique-key-compatibility';
import { STYLE_BUTTON_AS_LINK } from '@/utils/ui';
import { PRIMITIVES } from '@calm/evexia-design-tokens';
import settingsIcon from '@rhg/../public/_n/images/settings.png';
import { useCookieResponse } from '@rhg/hooks/use-cookie-response';
import { useCurrentLanguage } from '@rhg/hooks/use-current-language';
import { Box, Button, Theme, Typography } from '@rhg/libs/material';
import Image from '@rhg/libs/next/image';
import { routePattern, routes } from '@rhg/utils/routes';
import { useRouter } from 'next/router';
import { ReactNode, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ChLink } from '../ch-link';
import { SheetDialog } from '../sheet-dialog';
import { cookieDialogMessages } from './messages';

export const enum CookieResponse {
    All = 'all',
    Essential = 'essential',
}

// These are passthrough pages and the user likely won't be able to interact with the dialog
const excludedPages = [
    routes.auth,
    routes.saml,
    routes.partnerMemberActivation,
    routePattern.partnerMemberActivationToken,
];

const styleCookieDialogPresentation = {
    zIndex: (theme: Theme) => theme.zIndex.cookieDialog,
};
const styleImage = {
    '& img': {
        width: 88,
        height: 88,
        borderRadius: (theme: Theme) =>
            PRIMITIVES.radius._12 / theme.semantics.Spacing[4],
    },
    mb: 4,
};

function CookieImage() {
    return (
        <Box sx={styleImage}>
            <Image
                src={settingsIcon}
                alt=""
                style={{
                    height: 'unset',
                }}
            />
        </Box>
    );
}

function CookieDialogContent() {
    const { formatMessage } = useIntl();
    const currentLanguage = useCurrentLanguage();
    const { wrapNodesWithKeys } = useReact19UniqueKeyCompatibility();
    return (
        <>
            <Typography variant="body1" color="text.body" textAlign="center">
                {wrapNodesWithKeys(
                    formatMessage(cookieDialogMessages.body, {
                        CookieLink: (...chunks: ReactNode[]) => (
                            <Typography variant="body1Demi">
                                <Button
                                    variant="text"
                                    component={ChLink}
                                    sx={STYLE_BUTTON_AS_LINK}
                                    trackButton="NotMapped"
                                    href={routes.external.cookieNotice(
                                        currentLanguage
                                    )}
                                    target="_blank"
                                >
                                    {chunks}
                                </Button>
                            </Typography>
                        ),
                    })
                )}
            </Typography>
        </>
    );
}

interface CookieDialogProps {
    isOpen: boolean;
    onClose: (cookieAcceptance?: CookieResponse) => void;
}
/**
 * This component does not use the CookieResponseContext
 * This component will not be mapped in Amplitude
 */
export function CookieDialog({ isOpen, onClose }: CookieDialogProps) {
    const { formatMessage } = useIntl();
    if (!isOpen) {
        return null;
    }

    return (
        <SheetDialog
            trackDialog="NotMapped"
            onClose={onClose}
            title={formatMessage(cookieDialogMessages.title)}
            hideCloseButton
            stackBottomActions
            sx={styleCookieDialogPresentation}
            feature={<CookieImage />}
            content={<CookieDialogContent />}
            buttons={[
                {
                    variant: 'contained',
                    title: formatMessage(cookieDialogMessages.acceptAll),
                    trackButton: 'NotMapped',
                    onClick: () => onClose(CookieResponse.All),
                },
                {
                    variant: 'contained',
                    title: formatMessage(cookieDialogMessages.essentialOnly),
                    trackButton: 'NotMapped',
                    onClick: () => onClose(CookieResponse.Essential),
                },
            ]}
        />
    );
}

/** This component uses the CookieResponseContext */
export function CookieResponseDialog() {
    const { pathname } = useRouter();
    const { isResponseRequested, isFetched, setResponse } = useCookieResponse();

    const onClose = useCallback(
        (cookieResponse?: CookieResponse) => {
            if (cookieResponse) {
                setResponse(cookieResponse);
            }
        },
        [setResponse]
    );

    const isOpen = useMemo(
        () =>
            !excludedPages.includes(pathname) &&
            isFetched &&
            isResponseRequested,
        [isFetched, isResponseRequested, pathname]
    );

    return <CookieDialog isOpen={isOpen} onClose={onClose} />;
}
